import { ArrowRight, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";

const products = [
  {
    id: 1,
    name: "Jute Shopping Bags",
    description: "Durable, eco-friendly shopping bags perfect for retail and grocery stores.",
    image: "https://images.unsplash.com/photo-1758708536099-9f46dc81fffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwanV0ZSUyMHNob3BwaW5nJTIwYmFnJTIwbmF0dXJhbCUyMGxpZ2h0JTIwc3R1ZGlvfGVufDB8fHx8MTc3MjA4OTU3NHww&ixlib=rb-4.1.0&q=85",
    category: "Jute",
  },
  {
    id: 2,
    name: "Cotton Tote Bags",
    description: "Lightweight canvas tote bags ideal for promotional events and daily use.",
    image: "https://images.pexels.com/photos/9869067/pexels-photo-9869067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    category: "Cotton",
  },
  {
    id: 3,
    name: "Wine Bottle Bags",
    description: "Elegant jute wine carriers for gifting and retail wine stores.",
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=800",
    category: "Jute",
  },
  {
    id: 4,
    name: "Beach & Vacation Bags",
    description: "Spacious and stylish bags perfect for beach trips and vacations.",
    image: "https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=800",
    category: "Jute",
  },
  {
    id: 5,
    name: "Drawstring Pouches",
    description: "Versatile cotton drawstring bags for packaging and storage.",
    image: "https://images.unsplash.com/photo-1605518216938-7c31b7b14ad0?w=800",
    category: "Cotton",
  },
  {
    id: 6,
    name: "Gift & Promotional Bags",
    description: "Custom printed bags for corporate gifting and brand promotions.",
    image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800",
    category: "Custom",
  },
];

const categories = [
  {
    id: "jute-bags",
    title: "Jute Bags",
    description:
      "Our eco-friendly jute bags are made from natural, biodegradable fibers, making them a sustainable alternative to plastic packaging. Known for their strength and rustic appeal, jute bags are perfect for grocery stores, exhibitions, trade shows, and eco-conscious brands. They offer durability, reusability, and a premium handcrafted look.",
    image: process.env.PUBLIC_URL + "/totebag.png",
  },
  {
    id: "tote-bags",
    title: "Tote Bags",
    description:
      "Our premium tote bags are designed for everyday versatility and durability. Crafted from high-quality sustainable fabrics, they are ideal for retail, shopping, corporate gifting, and promotional use. With spacious interiors and strong handles, these bags combine functionality with a clean, modern aesthetic—perfect for brands that value style and sustainability.",
    image: process.env.PUBLIC_URL + "/bag2.png",
  },
  {
    id: "drawstring-bags",
    title: "Drawstring Bags",
    description:
      "Our drawstring bags are practical, lightweight, and easy to carry. Designed for convenience, they are perfect for sports events, giveaways, packaging, and everyday essentials. With secure closure and customizable branding options, these bags are a popular choice for businesses looking for affordable yet stylish packaging solutions.",
    image: process.env.PUBLIC_URL + "/bag3.png",
  },
  {
    id: "canvas-bags",
    title: "Canvas Bags",
    description:
      "Durable, lightweight, and highly customizable—our canvas bags are built for long-term use. Made from sturdy cotton canvas material, they are ideal for custom printing, branding, and promotional campaigns. These bags provide a balance of strength and elegance, suitable for retail stores, events, and corporate merchandising.",
    image: process.env.PUBLIC_URL + "/bag4.png",
  },
];

const CategoryCard = ({ image, title, description }) => (
  <div className="bg-white rounded-2xl overflow-hidden border border-sand/20 shadow-card group flex flex-col">
    <div className="relative h-56 md:h-64 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-heading text-xl font-semibold text-forest mb-2">
        {title}
      </h3>
      <p className="text-muted-foreground text-sm mb-4 flex-1">
        {description}
      </p>
      <Button
        variant="outline"
        className="self-start border-2 border-sand text-sand hover:bg-sand hover:text-white rounded-full px-6 transition-all duration-300"
      >
        View Products
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </div>
);

export const ProductsPage = ({ onOpenCatalogModal }) => {
  return (
    <div className="bg-cream">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-forest mb-6">
              Our <span className="text-sand">Collections</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore our comprehensive range of eco-friendly jute and cotton bags.
              From shopping bags to custom promotional items, we offer solutions
              for every need.
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest">
                Product Categories
              </h2>
              <p className="text-muted-foreground mt-2 max-w-2xl text-sm md:text-base">
                Explore our key mockup categories showcasing cotton and jute bags
                designed for corporate gifting, specialty packaging, and premium
                retail.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                image={category.image}
                title={category.title}
                description={category.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Custom Products CTA */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <div className="w-20 h-20 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-forest" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest mb-4">
            Need Custom Products?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            We specialize in OEM and private label manufacturing. Get bags designed
            specifically for your brand with custom sizes, colors, prints, and
            packaging.
          </p>
          <Button
            onClick={onOpenCatalogModal}
            className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
          >
            Download Full Catalog
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};
